// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { ErrorInformationStorage } from "../../Commons/ErrorInformationStorage.ts";
import { Resources } from "../../Commons/properties/Resources.ts";
import { Rectangle } from "../../Commons/Geometry.ts";
import { Cursors } from "../../Commons/Cursors.ts";
import { LinkedSystem } from "../misc/LinkedSystem.js";
import { GridPanelTemplate } from "../../templates.js";
import { TerceraInstrumentLookupDropDownForm } from "../elements/Lookup/TerceraInstrumentLookupDropDownForm.js";
import { TerceraLinkControlConstants } from "../UtilsClasses/TerceraLinkControlConstants.js";
import { LookupDropDownShowParams } from "../UtilsClasses/LookupDropDownShowParams.js";
import { ApplicationPanelNew } from "./ApplicationPanelNew.js";
import { PanelLocKeys, PanelNames } from "../UtilsClasses/FactoryConstants.ts";
import { ThemeManager } from "../misc/ThemeManager.js";
import { DynProperty } from "../../Commons/DynProperty.ts";
import { DataCache } from "../../Commons/DataCache.ts";
import { TerceraSymbolLookupBaseDataProvider } from "../../Commons/NoNFixedListCore.ts";
import { MainWindowManager } from "../UtilsClasses/MainWindowManager.js";
import { TerceraLookup } from "../elements/Lookup/TerceraLookup.js";
import { GridPanelEdit } from "../elements/GridPanelEdit.ts";
import { IsAllowedPanel } from "../WorkSpace/WorkSpaceUtils.ts";

export let GridPanel = ApplicationPanelNew.extend({
    partials: { bodyPartial: GridPanelTemplate },
    data: function ()
    {
        return {
            symbolLinkValue: TerceraLinkControlConstants.STATE_ALL,
            accountlLinkValue: TerceraLinkControlConstants.STATE_ALL,

            panelTypeEnum: PanelNames,

            panelType: PanelNames.ChartPanel,
            rows: 2,
            columns: 2,
            instrumentArray: [],
            cursor: Cursors.Default
        };
    },

    // TODO. Move to data.
    grid: null,

    fixedSizePanelMode: false,

    resizeData: null,
    resizingNow: false,

    mouseDownClientOffsetDeltaX: 0,
    mouseDownClientOffsetDeltaY: 0,

    topPanelHeight: 0,
    marginBetweenPanels: 5
});

GridPanel.prototype.getType = function () { return PanelNames.GridPanel };

GridPanel.prototype.oncomplete = function ()
{
    ApplicationPanelNew.prototype.oncomplete.apply(this);

    this.dataProvider = new TerceraSymbolLookupBaseDataProvider();

    this.onEditCallBack = this.onEditCallBack.bind(this);

    this.on('editClick', this.onEditClick);
    this.on('refreshChartClick', this.onRefreshChartClick);

    this.on('gridMouseDown', this.onGridMouseDown);
    this.on('gridMouseMove', this.onGridMouseMove);
    this.on('gridMouseUp', this.onGridMouseUp);

    this.observe('forbiddenPanel', this.onForbiddenPanelChanged);
    // TODO. SHIT CODE.
    if (!this.get('forbiddenPanel'))
    {
        if (!this.tryRestorePanels())
            this.repopulateWithPanels();
    }

    this.themeChange();
    this.localize();
};

GridPanel.prototype.onForbiddenPanelChanged = function (forbiddenPanel)
{
    if (forbiddenPanel)
        this.removeAllControls();
};

GridPanel.prototype.TickAsync = function ()
{
    var panels = this.Controls;
    for (var key in panels)
    {
        var panel = panels[key];
        if (panel.TickAsync)
            panel.TickAsync();
    }
};

GridPanel.prototype.repopulate = function ()
{
    const panels = this.Controls;
    for (var key in panels)
    {
        const panel = panels[key];
        if (IsAllowedPanel(panel)) {
            if (panel.repopulate)
                panel.repopulate();
        } else {
            panel.set('forbiddenPanel', true);
        }
    }
};

GridPanel.prototype.updateSettings = function ()
{
    var panels = this.Controls;
    for (var key in panels)
    {
        var panel = panels[key];
        if (panel.updateSettings)
            panel.updateSettings();
    }
};

GridPanel.prototype.themeChange = function ()
{
    if (this.get('forbiddenPanel'))
        return;

    var panels = this.Controls;
    for (var key in panels)
    {
        var panel = panels[key];
        if (panel.themeChange)
            panel.themeChange();
    }
};

GridPanel.prototype.localize = function ()
{
    ApplicationPanelNew.prototype.localize.apply(this);

    var headerLocKey = PanelLocKeys[PanelNames.GridPanel];
    this.set('header', Resources.getResource(headerLocKey));

    if (this.get('forbiddenPanel'))
        return;

    var panels = this.Controls;
    for (var key in panels)
    {
        var panel = panels[key];
        if (panel.localize)
            panel.localize();
    }
};

//#region Xml Settings

GridPanel.prototype.getXmlSettingsTemplate = function ()
{
    var xml = {};

    var xmlSettingsTemplateDict = {};
    var serializedPanelPropsDict = {};

    var panels = this.Controls;
    for (var key in panels)
    {
        var panel = panels[key];
        if (panel.getXmlSettingsTemplate && panel.Properties)
        {
            var panelName = panel.get('name');
            xmlSettingsTemplateDict[panelName] = panel.getXmlSettingsTemplate();
            serializedPanelPropsDict[panelName] =
                DynProperty.serialize(panel.Properties());
        }
    }

    xml.xmlSettingsTemplateDict = xmlSettingsTemplateDict;
    xml.serializedPanelPropertiesDict = serializedPanelPropsDict;
    xml.grid = this.grid;
    xml.panelType = this.get('panelType');
    xml.rows = this.get('rows');
    xml.columns = this.get('columns');

    return xml;
};

GridPanel.prototype.setXmlSettingsTemplate = function (value)
{
    if (!value) return;
    // TODO. SHIT CODE.
    this.restoreData = value;
};

// TODO. SHIT CODE.
GridPanel.prototype.tryRestorePanels = function ()
{
    try
    {
        var restoreData = this.restoreData;
        delete this.restoreData;

        if (!restoreData) return false;

        var panelType = restoreData.panelType;
        if (!panelType) return false;

        var rows = restoreData.rows;
        var columns = restoreData.columns;

        this.set({ rows: rows, columns: columns, panelType: panelType });
        this.repopulateWithPanels();

        var panels = this.Controls;
        var currentGrid = this.grid;

        var restoreGrid = restoreData.grid || currentGrid;

        var serializedPanelPropertiesDict = restoreData.serializedPanelPropertiesDict;
        var xmlSettingsTemplateDict = restoreData.xmlSettingsTemplateDict;

        for (var r_i = 0; r_i < rows; r_i++)
        {
            for (var c_i = 0; c_i < columns; c_i++)
            {
                var restoreGridItem = restoreGrid[r_i][c_i];
                var currentGridItem = currentGrid[r_i][c_i];

                currentGridItem.horizontalRatio = restoreGridItem.horizontalRatio;
                currentGridItem.verticalRatio = restoreGridItem.verticalRatio;

                var panelName = currentGridItem.panelName;
                var panel = panels[panelName];

                var panelSerializedPropArr = serializedPanelPropertiesDict[panelName];
                if (panelSerializedPropArr)
                    panel.callBack(DynProperty.deserialize(panelSerializedPropArr));

                var panelXmlSettingsTemplate = xmlSettingsTemplateDict[panelName];
                if (panelXmlSettingsTemplate)
                    panel.setXmlSettingsTemplate(panelXmlSettingsTemplate);
            }
        };

        this.layoutTable();

        return true;
    }
    catch (ex) 
    {
        ErrorInformationStorage.GetException(ex);
        console.log(ex);
    }

    return false;
};

//#endregion

//#region Symbol/Account Link In

GridPanel.prototype.symbolLink_In = function (symbolName)
{
    var curLinkColor = LinkedSystem.currentLinkOutColor;
    var panels = this.Controls;
    for (var key in panels)
    {
        var panel = panels[key];
        var panelColor = panel.get('symbolLinkValue');

        if (!panel.symbolLink_In)
            continue;

        if (panelColor !== curLinkColor &&
            panelColor !== TerceraLinkControlConstants.ColorConstants.ALL)
            continue;

        panel.symbolLink_In(symbolName);
    }
};

GridPanel.prototype.accountLink_In = function (accId, fromLinkedSystem)
{
    if (fromLinkedSystem && !this.OnAccountLinking()) { return; }

    var panels = this.Controls;
    for (var key in panels)
    {
        var panel = panels[key];

        if (!panel.accountLink_In)
            continue;
        panel.accountLink_In(accId);
    }
};

GridPanel.prototype.updateAccountLinkVisible = function (visible)
{
    ApplicationPanelNew.prototype.updateAccountLinkVisible.call(this, visible);

    Object.values(this.Controls).forEach(panel => {
        if (panel.updateAccountLinkVisible != null) {
            panel.updateAccountLinkVisible(visible);
        }
    });
};

//#endregion

GridPanel.prototype.getClientPanel = function ()
{
    return this.find('.grid-panel-container');
};

//#region Layout

GridPanel.prototype.layoutTable = function ()
{
    if (this.get('forbiddenPanel'))
        return;

    if (this.fixedSizePanelMode)
        this.layoutFixedSizePanels();
    else
        this.layoutResizablePanels();
};

GridPanel.prototype.layoutResizablePanels = function ()
{
    var grid = this.grid;
    if (!grid) return;

    var panels = this.Controls;

    var rows = this.get('rows');
    var columns = this.get('columns');
    var marginBetweenPanels = this.marginBetweenPanels;

    var totalPanelsWidthInRow = this.get('width') - marginBetweenPanels * (columns + 1);
    var totalPanelsHeightInColumn = this.get('height') - 25 - marginBetweenPanels * (rows + 1);

    var top = marginBetweenPanels;
    for (var r_i = 0; r_i < rows; r_i++)
    {
        var left = marginBetweenPanels;
        for (var c_i = 0; c_i < columns; c_i++)
        {
            var gridItem = grid[r_i][c_i];

            var newPanelW = gridItem.horizontalRatio * totalPanelsWidthInRow;
            var newPanelH = gridItem.verticalRatio * totalPanelsHeightInColumn;

            this.setPanelRect(
                panels[gridItem.panelName],
                left,
                top,
                newPanelW,
                newPanelH);

            left += newPanelW + marginBetweenPanels;
        }

        top += newPanelH + marginBetweenPanels;
    }
};

GridPanel.prototype.layoutFixedSizePanels = function ()
{
    var grid = this.grid;
    if (!grid) return;

    var panels = this.Controls;

    var rows = this.get('rows');
    var columns = this.get('columns');

    var minMarginBetweenPanels = this.marginBetweenPanels;

    var panelRect = this.getPanelRect(panels[grid[0][0].panelName]);
    var panelW = panelRect.Width;
    var panelH = panelRect.Height;

    var availableContainerW = this.get('width');
    var availableContainerH = this.get('height') - 25;

    var totalPanelsW = panelW * columns;
    var totalPanelsH = panelH * rows;

    var horizontalMarginBetweenPanels = Math.max(
        (availableContainerW - totalPanelsW) / (columns + 1),
        minMarginBetweenPanels);

    var verticalMarginBetweenPanels = Math.max(
        (availableContainerH - totalPanelsH) / (rows + 1),
        minMarginBetweenPanels);

    var top = verticalMarginBetweenPanels;
    for (var r_i = 0; r_i < rows; r_i++)
    {
        var left = horizontalMarginBetweenPanels;
        for (var c_i = 0; c_i < columns; c_i++)
        {
            var gridItem = grid[r_i][c_i];
            var panel = panels[gridItem.panelName];

            this.setPanelRect(
                panel,
                left,
                top,
                panelW,
                panelH);

            left += panelW + horizontalMarginBetweenPanels;
        }

        top += panelH + verticalMarginBetweenPanels;
    }
};

//#endregion

GridPanel.prototype.repopulateWithPanels = function ()
{
    this.clearContainerPanels();
    // Except buttons.
    this.removeAllControls({
        gridEditButton: true,
        refreshButton: true
    });

    var panelType = this.get('panelType');
    var rows = this.get('rows');
    var columns = this.get('columns');

    var grid = [];

    for (var r_i = 0; r_i < rows; r_i++)
    {
        var curGridRowArray = [];
        grid.push(curGridRowArray);

        for (var c_i = 0; c_i < columns; c_i++)
        {
            var panel = GridPanel.getPanel(panelType);
            if (!panel) {
                continue;
            }

            if (!IsAllowedPanel(panel)) {
                panel.set('forbiddenPanel', true);
            }

            panel.set({ name: GridPanel.createPanelName(r_i, c_i) });
            panel.set('style_addition_header', 'js-PropertySetupScreen-AdditionalHeader');
            this.addControl(panel);
            MainWindowManager.Factory.addPanelFromContainerToWs(panel);

            curGridRowArray.push(new GridItem(panel.get('name')));
        }
    }

    this.grid = grid;
    this.fixedSizePanelMode = false;
    this.setDefaultPanelsLayout();
    this.setInstruments();
};

GridPanel.prototype.updatePanelsSizeRatios = function ()
{
    var rows = this.get('rows');
    var columns = this.get('columns');

    var totalPanelsWidthInRow = this.get('width') - this.marginBetweenPanels * (columns + 1);
    var totalPanelsHeightInColumn = this.get('height') - 25 - this.marginBetweenPanels * (rows + 1);

    var grid = this.grid;
    var panels = this.Controls;

    for (var r_i = 0; r_i < rows; r_i++)
    {
        var gridRow = grid[r_i];
        for (var c_i = 0; c_i < columns; c_i++)
        {
            var gridItem = gridRow[c_i];
            var panel = panels[gridItem.panelName];
            var panelRect = this.getPanelRect(panel);

            gridItem.horizontalRatio = panelRect.Width / totalPanelsWidthInRow;
            gridItem.verticalRatio = panelRect.Height / totalPanelsHeightInColumn;
        }
    }
};

GridPanel.prototype.setInstruments = function ()
{
    var instrumentArray = this.get('instrumentArray');
    if (!instrumentArray || !instrumentArray.length)
        return;

    var grid = this.grid;
    var rows = this.get('rows');
    var columns = this.get('columns');

    var panels = this.Controls;
    for (var r_i = 0; r_i < rows; r_i++)
    {
        for (var c_i = 0; c_i < columns; c_i++)
        {
            var instrument = instrumentArray[r_i * columns + c_i];
            if (!instrument) return;

            panels[grid[r_i][c_i].panelName].symbolLink_In(instrument.GetInteriorID());
        }
    }
};

GridPanel.prototype.setDefaultPanelsLayout = function ()
{
    var grid = this.grid;
    var rows = this.get('rows');
    var columns = this.get('columns');

    var VRatio = 1 / rows;
    var HRatio = 1 / columns;

    for (var r_i = 0; r_i < rows; r_i++)
    {
        var gridRow = grid[r_i];
        for (var c_i = 0; c_i < columns; c_i++)
        {
            var gridItem = gridRow[c_i];
            gridItem.verticalRatio = VRatio;
            gridItem.horizontalRatio = HRatio;
        }
    }

    this.layoutTable();
};

// TODO. SHIT CODE.
GridPanel.prototype.resize = function (curX, curY)
{
    var resizeData = this.resizeData;

    var verticalResize = resizeData.verticalResize;

    var resizeAreaRect = resizeData.resizeAreaRect;
    var resizeAreaHitX = resizeData.resizeAreaHitX;
    var resizeAreaHitY = resizeData.resizeAreaHitY;

    var minStartSideValue = resizeData.minStartSideValue;
    var maxStartSideValue = resizeData.maxStartSideValue;

    var deltaX = curX - resizeAreaHitX;
    var deltaY = curY - resizeAreaHitY;

    if (verticalResize)
    {
        var startSideBottom = resizeAreaRect.Y + deltaY;
        if (startSideBottom < minStartSideValue)
            startSideBottom = minStartSideValue;
        else if (startSideBottom > maxStartSideValue)
            startSideBottom = maxStartSideValue;

        var endSideY = startSideBottom + resizeAreaRect.Height;
    }
    else
    {
        var startSideRight = resizeAreaRect.X + deltaX;
        if (startSideRight < minStartSideValue)
            startSideRight = minStartSideValue;
        else if (startSideRight > maxStartSideValue)
            startSideRight = maxStartSideValue;

        var endSideX = startSideRight + resizeAreaRect.Width;
    }

    var panels = this.Controls;

    var startSideGridItemsArray = resizeData.startSideGridItemsArray;
    var len = startSideGridItemsArray.length;
    for (var i = 0; i < len; i++)
    {
        var gridItem = startSideGridItemsArray[i];
        var panel = panels[gridItem.panelName];
        var panelRect = this.getPanelRect(panel);

        if (verticalResize)
            panelRect.Height = startSideBottom - panelRect.Y;
        else
            panelRect.Width = startSideRight - panelRect.X;

        this.setPanelRect(
            panel,
            panelRect.X,
            panelRect.Y,
            panelRect.Width,
            panelRect.Height);
    }

    var endSideGridItemsArray = resizeData.endSideGridItemsArray;
    var len = endSideGridItemsArray.length;
    for (var i = 0; i < len; i++)
    {
        var gridItem = endSideGridItemsArray[i];
        var panel = panels[gridItem.panelName];

        var panelRect = this.getPanelRect(panel);

        if (verticalResize)
        {
            var oldPanelY = panelRect.Y;
            panelRect.Y = endSideY;
            panelRect.Height += oldPanelY - endSideY;
        }
        else
        {
            var oldPanelX = panelRect.X;
            panelRect.X = endSideX;
            panelRect.Width += oldPanelX - endSideX;
        }

        this.setPanelRect(
            panel,
            panelRect.X,
            panelRect.Y,
            panelRect.Width,
            panelRect.Height);
    }
};

//#region Grid Mouse Processing

GridPanel.prototype.onGridMouseDown = function (e)
{
    var event = e.original;
    if (!event.target.classList.contains('grid-panel-container'))
        return;

    this.resizeData = this.getGridResizeData(event.offsetX, event.offsetY);
    if (this.resizeData)
    {
        this.resizingNow = true;
        this.mouseDownClientOffsetDeltaX = event.clientX - event.offsetX;
        this.mouseDownClientOffsetDeltaY = event.clientY - event.offsetY;
    }
};

GridPanel.prototype.onGridMouseMove = function (e)
{
    var event = e.original;

    if (this.resizingNow)
    {
        // event.offsetX/offsetY may be passed from a grid panel while moving cursor.
        var offsetX = event.clientX - this.mouseDownClientOffsetDeltaX;
        var offsetY = event.clientY - this.mouseDownClientOffsetDeltaY;
        this.resize(offsetX, offsetY);
        return;
    }

    if (!event.target.classList.contains('grid-panel-container'))
    {
        this.set('cursor', Cursors.Default);
        return;
    }

    var gridResizeData = this.getGridResizeData(event.offsetX, event.offsetY);
    if (gridResizeData)
    {
        this.set('cursor',
            gridResizeData.verticalResize ?
                // TODO. Rename.
                Cursors.HSplit :
                Cursors.VSplit);
        return;
    }

    this.set('cursor', Cursors.Default);
};

GridPanel.prototype.onGridMouseUp = function ()
{
    if (this.resizingNow)
        this.updatePanelsSizeRatios();

    this.resizingNow = false;
    this.resizeData = null;
};

//#endregion

//#region Get Grid Resize Data TODO. SHIT CODE.

GridPanel.prototype.getGridResizeData = function (x, y)
{
    if (this.fixedSizePanelMode)
        return null;

    return this.getVerticalResizeData(x, y) || this.getHorizontalResizeData(x, y);
};

GridPanel.prototype.getVerticalResizeData = function (x, y)
{
    var grid = this.grid;
    var panels = this.Controls;
    var marginBetweenPanels = this.marginBetweenPanels;
    var verticalResizeAreaW = this.get('width') - 2 * marginBetweenPanels;

    var r_len = grid.length;
    for (var r_i = 0; r_i < r_len - 1; r_i++)
    {
        var curGridItem = grid[r_i][0];
        var curPanel = panels[curGridItem.panelName];
        var curPanelRect = this.getPanelRect(curPanel);

        var resizeAreaRect = new Rectangle(
            marginBetweenPanels,
            curPanelRect.Bottom(),
            verticalResizeAreaW,
            marginBetweenPanels);

        if (!resizeAreaRect.Contains(x, y))
            continue;

        var nextGridItem = grid[r_i + 1][0];
        var nextPanel = panels[nextGridItem.panelName];
        var nextPanelRect = this.getPanelRect(nextPanel);

        var minPanelH = 25;

        var minStartSideY = curPanelRect.Y + minPanelH;
        var maxStartSideY = nextPanelRect.Bottom() - minPanelH;

        return new GridResizeData(
            true,
            resizeAreaRect,
            x,
            y,
            grid[r_i],
            grid[r_i + 1],
            minStartSideY,
            maxStartSideY);
    }

    return null;
};

GridPanel.prototype.getHorizontalResizeData = function (x, y)
{
    var grid = this.grid;
    var panels = this.Controls;
    var marginBetweenPanels = this.marginBetweenPanels;

    var r_len = grid.length;
    for (var r_i = 0; r_i < r_len; r_i++)
    {
        var gridRow = grid[r_i];
        var c_len = gridRow.length;
        for (var c_i = 0; c_i < c_len - 1; c_i++)
        {
            var curGridItem = gridRow[c_i];
            var curPanel = panels[curGridItem.panelName];
            var curPanelRect = this.getPanelRect(curPanel);

            var resizeAreaRect = new Rectangle(
                curPanelRect.Right(),
                curPanelRect.Y,
                marginBetweenPanels,
                curPanelRect.Height);

            if (!resizeAreaRect.Contains(x, y))
                continue;

            var nextGridItem = gridRow[c_i + 1];
            var nextPanel = panels[nextGridItem.panelName];
            var nextPanelRect = this.getPanelRect(nextPanel);

            var minPanelW = 25;

            var minStartSideX = curPanelRect.X + minPanelW;
            var maxStartSideX = nextPanelRect.Right() - minPanelW;

            return new GridResizeData(
                false,
                resizeAreaRect,
                x,
                y,
                [curGridItem],
                [nextGridItem],
                minStartSideX,
                maxStartSideX);
        }
    }

    return null;
};

//#endregion

//#region Get/Set Panel Rect

// - 2 - borderline;
GridPanel.prototype.setPanelRect = function (panel, x, y, width, height)
{
    // TODO. Use setBounds(); at this moment it doesn't call layoutTable();
    panel.set({ left: x, top: y });
    panel.setSize(width - 2, height - 2);
};

// + 2 - borderline;
GridPanel.prototype.getPanelRect = function (panel)
{
    return new Rectangle(
        panel.get('left') || 0,
        panel.get('top') || 0,
        (panel.get('width') || 0) + 2,
        (panel.get('height') || 0) + 2);
};

//#endregion

GridPanel.prototype.onRefreshChartClick = function ()
{
    var panels = this.Controls;
    for (var key in panels)
    {
        var terceraChartRactive = panels[key].terceraChartRactive;
        var chart = terceraChartRactive ? terceraChartRactive.terceraChart : null;
        if (chart) chart.RefreshChart();
    }
};

GridPanel.prototype.onEditClick = function (button)
{
    GridPanelEdit.show(
        button.getX(),
        button.getY() + 26,
        {
            rows: this.get('rows'),
            columns: this.get('columns'),
        },
        this.onEditCallBack
    );
};

GridPanel.prototype.instrumentsDropDownCallback = function (instrumentArray)
{
    this.set('instrumentArray', instrumentArray);
    this.setInstruments();
};

GridPanel.prototype.onEditCallBack = function (data)
{
    if (data.hasOwnProperty('rows') && data.hasOwnProperty('columns'))
    {
        this.set({ rows: data.rows, columns: data.columns });
        this.repopulateWithPanels();
    }
    else if (data.hasOwnProperty('panelType'))
    {
        this.set('panelType', data.panelType);
        this.repopulateWithPanels();
    }
    else if (data.resetLayout)
    {
        this.setDefaultPanelsLayout();
    }
};

GridPanel.prototype.updatePanelHeader = function () { };

GridPanel.prototype.clearContainerPanels = function () {
    for (const key in this.Controls) {
        const panel = this.Controls[key];
        if (panel.getType() === PanelNames.ChartPanel) {
            MainWindowManager.Factory.removePanelFromContainerToWs(panel._guid);
        }
    }
}
GridPanel.prototype.dispose = function () {
    this.clearContainerPanels();
    ApplicationPanelNew.prototype.dispose.call(this);
}

GridPanel.getPanel = function (panelType)
{
    const panel = MainWindowManager.Factory.createPanel(panelType);
    if (panel)
    {
        // TODO. Same as EmbeddedApplicationPanelNew?
        panel.set({
            isSetFocusOnComplete: false,
            withOutBorder: false,
            showHeader: true,
            showFooter: false,
            movable: false,
            resizable: false,
            dockablePanel: false,
            closeBtnVisible: false
        });
    }
    return panel;
};

GridPanel.createPanelName = function (rowIdx, columnIdx)
{
    return rowIdx + '_' + columnIdx;
};

//#region Custom Objects

let GridItem = function (panelName)
{
    this.panelName = panelName;
    this.horizontalRatio = 0;
    this.verticalRatio = 0;
};

let GridResizeData = function (
    verticalResize,
    resizeAreaRect,
    resizeAreaHitX,
    resizeAreaHitY,
    startSideGridItemsArray,
    endSideGridItemsArray,
    minStartSideValue,
    maxStartSideValue)
{
    this.verticalResize = verticalResize;
    this.resizeAreaRect = resizeAreaRect;
    this.resizeAreaHitX = resizeAreaHitX;
    this.resizeAreaHitY = resizeAreaHitY;
    this.startSideGridItemsArray = startSideGridItemsArray;
    this.endSideGridItemsArray = endSideGridItemsArray;

    // Min start panel's x/y.
    this.minStartSideValue = minStartSideValue;
    // Max start panel's x/y.
    this.maxStartSideValue = maxStartSideValue;
};

//#endregion